import { WarningFilled } from "@ant-design/icons";
import { Button, Form, Input, Modal } from "antd";
import { ChangeEvent, useState } from "react";

export type InputEditDataType = {
  value: any;
  id: string;
  type?: string;
  lable?: string;
  warningMessage?: string;
  identityName?: string;
};

type RenamePopupProps = {
  inputData: InputEditDataType;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  onOk: (renamedText: string, id: string) => Promise<void>;
  title?: string;
  okName?: string;
};
function RenamePopup({
  inputData,
  isModalOpen,
  setIsModalOpen,
  onOk,
  title,
  okName
}: RenamePopupProps) {
  const [loading, setLoading] = useState(false);
  const originalValue =
    inputData.value === undefined || inputData.value === null
      ? ""
      : String(inputData.value);
  const [updatingString, setUpdatingString] = useState(originalValue);
  const [hasEdited, setHasEdited] = useState(false);

  const trimmedValue = updatingString.trim();
  const isEmpty = trimmedValue === "";
  const isNumeric = /^\d+$/.test(trimmedValue);
  const isUnchanged =
    isNumeric &&
    originalValue.trim() !== "" &&
    Number(trimmedValue) === Number(originalValue.trim());

  const validationMessage = isEmpty
    ? "Please enter duration."
    : !isNumeric
      ? "Please enter duration in numbers only."
      : isUnchanged && hasEdited
        ? "Duration is unchanged."
        : undefined;
  const isSaveDisabled = isEmpty || !isNumeric || isUnchanged;

  const handleOk = async () => {
    // The only caller is the footer OK button, which antd disables with this
    // exact same `isSaveDisabled` expression (a custom `footer` is supplied, so
    // Modal's own onOk button never renders) — the guard can never trip.
    /* v8 ignore next */
    if (isSaveDisabled) return;

    setLoading(true);
    try {
      await onOk(trimmedValue, inputData.id);
      setIsModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setUpdatingString(value);
    setHasEdited(true);
  };

  return (
    <Modal
      key={inputData.id}
      title={title || "Edit duration"}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      data-testid="rename-popup-modal"
      footer={[
        <Button
          key="back"
          onClick={handleCancel}
          data-testid="rename-popup-cancel-btn"
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={isSaveDisabled}
          loading={loading}
          onClick={handleOk}
          data-testid="rename-popup-ok-btn"
        >
          {okName || "Rename"}
        </Button>
      ]}
      destroyOnHidden
    >
      {inputData.warningMessage && (
        <div
          className="flex pb-4 text-xs items-center text-orange-500"
          data-testid="rename-popup-warning-msg"
        >
          <WarningFilled className="pr-2" /> {inputData.warningMessage}
        </div>
      )}
      <div
        className="flex w-full space-x-1"
        key={inputData.id}
        data-testid="rename-popup-form-wrapper"
      >
        <Form
          className="w-full"
          layout="vertical"
          data-testid="rename-popup-form"
        >
          <Form.Item
            label={`${inputData.lable || "New Name"} (in days)`}
            validateStatus={validationMessage ? "error" : undefined}
            help={validationMessage}
            data-testid="rename-popup-form-item"
          >
            <Input
              type={inputData.type || "text"}
              inputMode="numeric"
              value={updatingString}
              onChange={onInputChange}
              data-testid="rename-popup-input"
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}
export default RenamePopup;
